import { Component, Vue, namespace, Prop, Watch } from 'nuxt-property-decorator'
import { VPopover } from 'v-tooltip'
import { mdiInformationOutline, mdiChevronDown, mdiGiftOutline } from '@mdi/js'
import { IStyle } from '~/assets/interfaces/classes'
import { IFavouriteItem } from '~/assets/interfaces/favourites'
import {
  IProductAdditionalService,
  IProductToCart,
  IProductToFavourites,
} from '~/assets/interfaces/products'
import { ICartItem, ICartMessage } from '~/assets/interfaces/cart'
import { filterPrice } from '~/assets/filters/price'
import { filterBonus } from '~/assets/filters/bonus'
import { debounce } from '~/assets/functions/debounce'
import { ORDER_PATH } from '~/assets/config/path'
import { filterUrl } from '~/assets/filters/url'
import {
  API_CART_DELAY,
  API_CART_DELETE,
  API_CART_SERVICE,
} from '~/assets/config/api-path'
import ComponentIcon from '~/components/icon/icon.vue'
import ComponentImage from '~/components/images/image.vue'
import ComponentPrice from '~/components/price/price.vue'
import ComponentFormButton from '~/components/form/button.vue'
import ComponentOrderBuyOneClick from '~/components/products/buy-one-click.vue'
import ComponentFormCheckbox from '~/components/form/checkbox.vue'

const cartStore = namespace('cart')
const favouritesStore = namespace('favourites')
const siteStore = namespace('site')

/**
 * Общая логика корзины для десктопной (components/order/cart.vue)
 * и мобильной (components/mobile/order/m-cart.vue) версий.
 */
@Component({
  components: {
    ComponentImage,
    ComponentPrice,
    ComponentFormButton,
    ComponentLoadingDots: () => import('~/components/loading/dots.vue'),
    ComponentPopup: () => import('~/components/popup/popup.vue'),
    ComponentOrderBuyOneClick,
    ComponentAlert: () => import('~/components/alerts/alert.vue'),
    ComponentFormCheckbox,
    ComponentIcon,
    ComponentOrderCartShare: () => import('~/components/order/cart-share.vue'),
    VPopover,
  },
  filters: {
    filterPrice,
    filterBonus,
  },
})
export default class MixinCart extends Vue {
  @Prop({ type: Boolean, default: false }) readonly isCanBuyOneClick!: boolean

  @siteStore.Getter('getOptionsCurrency') protected getOptionsCurrency!: string
  @cartStore.Action('getItems') protected cartGetItems!: () => Promise<void>
  @cartStore.State('message') readonly message!: ICartMessage | null
  @cartStore.State('canOrder') readonly canOrder!: boolean
  @cartStore.State('bonus') readonly summaryBonus!: number
  @cartStore.Action('toCart') protected toCart!: (
    product: IProductToCart
  ) => Promise<void>

  @cartStore.State('items') protected cartItems!: ICartItem[]
  @cartStore.State('isFirstLoaded') protected isFirstLoaded!: boolean
  @favouritesStore.Action('toFavourites') protected toFavourites!: (
    product: IProductToFavourites
  ) => Promise<void>

  @cartStore.Getter('itemsQuantity') protected itemsQuantity!: number
  @favouritesStore.Action('deleteFavourites') protected deleteFavourites!: (
    product: IProductToFavourites
  ) => Promise<void>

  @favouritesStore.State('items') protected favouritesItems!: IFavouriteItem[]

  protected $style!: IStyle
  protected isShowBuyOneClick = false
  protected newItems: ICartItem[] | null = null
  protected isLoading = false
  protected mdiInformationOutline = mdiInformationOutline
  protected mdiChevronDown = mdiChevronDown
  protected mdiGiftOutline = mdiGiftOutline

  protected get checkAllProducts() {
    if (this.newItems) {
      let tempTrue = true
      this.newItems.forEach((item) => {
        tempTrue = tempTrue && item.active
      })
      return tempTrue
    }
    return false
  }

  protected set checkAllProducts(val: boolean) {
    ;(async () => {
      await this.delay2({
        id: -1,
        active: val,
      })
    })()
  }

  @Watch('cartItems', { deep: true, immediate: true })
  protected cartItemsHandler(val: ICartItem[]) {
    this.newItems = JSON.parse(JSON.stringify(val))
  }

  protected async delay(item: ICartItem) {
    this.isLoading = true
    await this.delay2({
      id: item.cartId,
      active: item.active,
    })
    this.isLoading = false
  }

  protected async delay2(item: { id: number; active: boolean }) {
    this.isLoading = true
    await this.$axios.$post(API_CART_DELAY, {
      id: item.id,
      active: item.active,
    })

    await this.cartGetItems()

    this.isLoading = false
  }

  protected isInFavourites(id: number) {
    return !!this.favouritesItems.find((item) => {
      return item.id === id
    })
  }

  protected toOrder() {
    this.$router.push(filterUrl(ORDER_PATH))
  }

  protected toFavouritesText(id: number) {
    return this.isInFavourites(id) ? 'В избранном' : 'В избранное'
  }

  protected mounted(): void {
    this.$emit('loaded')
  }

  protected classToFavourites(id: number) {
    return {
      [this.$style.additionalActionsItem]: true,
      [this.$style.active]: this.isInFavourites(id),
    }
  }

  protected get cartPrice() {
    let temp = 0
    this.activeCartItems.forEach((item) => {
      temp = temp + item.quantity * item.price
    })

    return temp
  }

  protected changeQuantity(
    id: number,
    cartId: number,
    quantity: number | string
  ) {
    this.changeQuantityDebounce(id, cartId, quantity, this)
  }

  protected changeQuantityDebounce: (
    id: number,
    cartId: number,
    quantity: number | string,
    that: this
  ) => void = debounce(
    async (
      id: number,
      cartId: number,
      quantity: number | string,
      that: this
    ) => {
      if (quantity.toString().trim().length !== 0) {
        that.isLoading = true
        await that.toCart({
          id: Number(id),
          cartId: Number(cartId),
          quantity: Number(quantity),
        })

        that.isLoading = false
      }
    },
    250
  )

  protected get activeCartItems() {
    return this.cartItems.filter((item) => item.active)
  }

  protected get activeItemsQuantity() {
    return this.activeCartItems.length
  }

  protected get cartFinalPrice() {
    let temp = 0
    this.activeCartItems.forEach((item) => {
      temp = temp + item.quantity * item.finalPrice
    })

    temp = temp + this.cartPriceServices

    return temp
  }

  protected async deleteItems() {
    this.isLoading = true
    await this.$axios.$post(API_CART_DELETE, {
      productId: -1,
    })
    await this.cartGetItems()
    this.isLoading = false
  }

  protected get cartPriceServices() {
    let temp = 0
    this.activeCartServiceItems.forEach((item) => {
      temp = temp + item.price
    })

    return temp
  }

  protected hiddenServices(item: ICartItem) {
    const index = this.hiddenServicesArray.indexOf(item.cartId)
    if (index >= 0) {
      this.hiddenServicesArray.splice(index, 1)
    } else {
      this.hiddenServicesArray.push(item.cartId)
    }
  }

  protected getQuantityServices(items: IProductAdditionalService[]) {
    return items.filter((item) => item.active).length
  }

  protected get servicePriceRender() {
    if (this.cartPriceServices === 0) {
      return 'Бесплатно'
    } else {
      return filterPrice(this.cartPriceServices) + this.getOptionsCurrency
    }
  }

  protected hiddenServicesArray: number[] = []

  protected IsInHiddenServicesArray(item: ICartItem) {
    return !this.hiddenServicesArray.includes(item.cartId)
  }

  protected async checkServices(
    item: ICartItem,
    service: IProductAdditionalService
  ) {
    this.isLoading = true
    await this.checkServices2({
      id: item.cartId,
      service: {
        id: service.id,
        active: service.active,
      },
    })
    this.isLoading = false
  }

  protected async checkServices2(item: {
    id: number
    service: {
      id: number
      active: boolean
    }
  }) {
    this.isLoading = true
    await this.$axios.$post(API_CART_SERVICE, {
      id: item.id,
      service: item.service,
    })

    await this.cartGetItems()

    this.isLoading = false
  }

  protected get activeCartServiceItems() {
    let services: IProductAdditionalService[] = []

    this.activeCartItems.forEach((item) => {
      if (item.services && item.services.length > 0) {
        const services2 = item.services.filter((item2) => item2.active)
        if (services2.length > 0) {
          services = services.concat(services2)
        }
      }
    })
    return services
  }

  protected get activeServiceItemsQuantity() {
    return this.activeCartServiceItems.length
  }
}
